import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Collapse, Dropdown, Empty, InputNumber, Modal, Row, Segmented, Select, Space, Spin, Switch, Tooltip, Typography, message, theme } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import { useTranslation } from '../../../shared/i18n';

const { Text } = Typography;
const ok = (r) => r && r.status === 'success';

export const VDS_ROLES = [
    { value: 'auto', label: '自动' },
    { value: 'reserved', label: '宿主保留' },
    { value: 'shared', label: '仅共享' },
    { value: 'vds', label: '仅VDS' },
];
const ROLE_STATE = { auto: 'free', reserved: 'reserved', shared: 'shared', vds: 'vds_free' };
const STATE_LABEL = { free: '空闲', reserved: '宿主保留', shared: '仅共享', vds_free: '仅VDS', held: '已占用' };

const stateStyle = (token) => ({
    free: { background: token.colorBgContainer, borderColor: token.colorBorder },
    reserved: { background: token.colorFillSecondary, borderColor: token.colorBorder },
    shared: { background: token.colorInfoBg, borderColor: token.colorInfoBorder },
    vds_free: { background: token.colorSuccessBg, borderColor: token.colorSuccessBorder },
    held: { background: token.colorWarningBg, borderColor: token.colorWarningBorder },
});

/** Admin: one node's VDS policy + core map (GET/POST /nodes/{id}/vds). */
const VdsNodePanel = ({ nodeId }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [roles, setRoles] = useState({});

    const apply = useCallback((d) => {
        const p = d.policy || {};
        setForm({ enabled: !!p.enabled, unit: p.unit || 'core', min_shared: p.min_shared || 0, hugepages_mb: p.hugepages_mb || 0, hugepage_kb: p.hugepage_kb || 2048, tags: p.tags || [] });
        const r = {};
        (d.map?.cpus || []).forEach((c) => { r[c.cpu] = c.role || 'auto'; });
        setRoles(r);
    }, []);

    const load = useCallback(async () => {
        if (!nodeId) return;
        setLoading(true);
        const r = await evmAdminService.getNodeVds(nodeId);
        setLoading(false);
        if (!ok(r)) { message.error(r?.msg || r?.error || t('Failed to load')); return; }
        setData(r.data);
        if (r.data?.available) apply(r.data);
    }, [nodeId, apply, t]);
    useEffect(() => { load(); }, [load]);

    const cpus = useMemo(() => data?.map?.cpus || [], [data]);
    const byCpu = useMemo(() => Object.fromEntries(cpus.map((c) => [c.cpu, c])), [cpus]);
    // NUMA node → physical cores (siblings together)
    const groups = useMemo(() => {
        const numa = {};
        cpus.forEach((c) => {
            const key = `${c.socket}-${c.core}`;
            const n = (numa[c.numa] = numa[c.numa] || {});
            (n[key] = n[key] || []).push(c);
        });
        return Object.entries(numa).sort((a, b) => a[0] - b[0])
            .map(([n, cores]) => [n, Object.values(cores).map((l) => l.sort((a, b) => a.cpu - b.cpu)).sort((a, b) => a[0].cpu - b[0].cpu)]);
    }, [cpus]);

    const setRole = (c, role) => {
        const ids = form.unit === 'core' && c.siblings?.length ? c.siblings : [c.cpu];
        setRoles((s) => { const n = { ...s }; ids.forEach((id) => { if (byCpu[id]) n[id] = role; }); return n; });
    };

    const save = async (confirm = false) => {
        const payload = {
            ...form,
            roles: Object.fromEntries(Object.entries(roles).filter(([, v]) => v && v !== 'auto')),
            ...(confirm ? { confirm: 1 } : {}),
        };
        setSaving(true);
        const r = await evmAdminService.saveNodeVds(nodeId, payload);
        setSaving(false);
        if (ok(r)) {
            message.success(r.data?.repinned?.length ? `${t('Saved')} · ${t('Re-pinned')} ${r.data.repinned.length}` : t('Saved'));
            load();
            return;
        }
        if (r?.data?.confirm && !confirm) {
            Modal.confirm({
                title: t('These VDS will be re-pinned live'),
                content: (
                    <ul style={{ paddingInlineStart: 18, margin: 0 }}>
                        {(r.data.affected || []).map((a) => (
                            <li key={a.vm_id}>{a.hostname || a.name || `#${a.vm_id}`} <Text type="secondary">#{a.vm_id} · CPU {(a.cpus || []).join(',')}</Text></li>
                        ))}
                    </ul>
                ),
                onOk: () => save(true),
            });
            return;
        }
        message.error(r?.msg || r?.error || t('Failed to save'));
    };

    if (!data) return <Spin spinning={loading}><div style={{ minHeight: 120 }} /></Spin>;
    if (!data.available) return <Empty description={t('Database not migrated')} />;

    const topo = data.topology;
    const styles = stateStyle(token);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const cell = (c) => {
        const changed = (roles[c.cpu] || 'auto') !== (c.role || 'auto');
        const st = c.state === 'held' ? 'held' : (changed ? ROLE_STATE[roles[c.cpu] || 'auto'] : c.state);
        const tip = [
            `CPU ${c.cpu} · core ${c.core} · NUMA ${c.numa}`,
            t(STATE_LABEL[st] || st),
            c.vm ? `${c.vm.hostname || c.vm.name} · vCPU ${c.vm.vcpu}${c.vm.manual ? ` · ${t('Manual')}` : ''}` : null,
        ].filter(Boolean).join(' · ');
        return (
            <Dropdown key={c.cpu} trigger={['click']} menu={{
                selectable: true, selectedKeys: [roles[c.cpu] || 'auto'],
                items: VDS_ROLES.map((o) => ({ key: o.value, label: t(o.label) })),
                onClick: ({ key }) => setRole(c, key),
            }}>
                <Tooltip title={tip}>
                    <div style={{
                        ...styles[st], borderWidth: 1, borderStyle: changed ? 'dashed' : 'solid', borderRadius: 4,
                        width: 64, height: 40, padding: '2px 4px', cursor: 'pointer', fontSize: 12, lineHeight: '17px', overflow: 'hidden',
                    }}>
                        <div><b>{c.cpu}</b>{roles[c.cpu] && roles[c.cpu] !== 'auto' && <Text type="secondary" style={{ fontSize: 10 }}> {t(VDS_ROLES.find((o) => o.value === roles[c.cpu])?.label)}</Text>}</div>
                        {c.vm && <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{c.vm.hostname || c.vm.name}:{c.vm.vcpu}</div>}
                    </div>
                </Tooltip>
            </Dropdown>
        );
    };

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space wrap size={16} style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Space wrap size={16}>
                        <Space><Text>{t('VDS enabled')}</Text><Switch checked={form.enabled} onChange={set('enabled')} /></Space>
                        <Segmented value={form.unit} onChange={set('unit')} options={[{ value: 'core', label: t('Whole core') }, { value: 'thread', label: t('Thread') }]} />
                    </Space>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load} />
                        <Button type="primary" loading={saving} onClick={() => save(false)}>{t('Save')}</Button>
                    </Space>
                </Space>

                {topo ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {[topo.model, t('{{n}} threads', { n: topo.cpus }), `${topo.sockets} socket`, `NUMA ×${topo.numa_nodes}`,
                            data.free ? t('Available {{c}} cores / {{th}} threads', { c: data.free.cores, th: data.free.threads }) : null].filter(Boolean).join(' · ')}
                    </Text>
                ) : <Alert type="warning" showIcon message={t('Node has not reported CPU topology (agent upgrade needed)')} />}

                {data.report?.errors?.length > 0 && <Alert type="warning" showIcon message={data.report.errors.join('；')} />}

                {groups.map(([numa, cores]) => (
                    <div key={numa}>
                        <Text strong style={{ fontSize: 12 }}>NUMA {numa}</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                            {cores.map((threads) => (
                                <div key={threads[0].cpu} style={{ display: 'flex', gap: 2, padding: 2, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 6 }}>
                                    {threads.map(cell)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                {cpus.length > 0 && (
                    <Space wrap size={12} style={{ fontSize: 12 }}>
                        {Object.entries(STATE_LABEL).map(([k, l]) => (
                            <Space key={k} size={4}><span style={{ ...styles[k], display: 'inline-block', width: 12, height: 12, border: '1px solid', borderRadius: 2 }} />{t(l)}</Space>
                        ))}
                    </Space>
                )}

                <Collapse ghost size="small" items={[{
                    key: 'adv', label: t('Advanced'), children: (
                        <Row gutter={12}>
                            <Col xs={24} sm={8}>
                                <Text>{t('Min shared-pool threads')}</Text>
                                <InputNumber min={0} value={form.min_shared || null} placeholder={String(data.min_shared_auto ?? 0)}
                                    onChange={(v) => set('min_shared')(v || 0)} style={{ width: '100%' }} />
                            </Col>
                            <Col xs={24} sm={8}>
                                <Text>{t('Hugepages MB')}</Text>
                                <Space.Compact style={{ width: '100%' }}>
                                    <InputNumber min={0} step={1024} value={form.hugepages_mb} onChange={(v) => set('hugepages_mb')(v || 0)} style={{ width: '100%' }} />
                                    <Select value={form.hugepage_kb} onChange={set('hugepage_kb')} style={{ width: 90 }}
                                        options={[{ value: 2048, label: '2 MB' }, { value: 1048576, label: '1 GB' }]} />
                                </Space.Compact>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Text>{t('Node tags')}</Text>
                                <Select mode="tags" value={form.tags} onChange={set('tags')} tokenSeparators={[',', ' ']} open={false} style={{ width: '100%' }} />
                            </Col>
                        </Row>
                    ),
                }]} />
            </Space>
        </Spin>
    );
};

export default VdsNodePanel;
